import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { API } from 'aws-amplify';
import useLeaderboardStore, {
  Result,
} from '../../../store/zustand/useLeaderboardStore';

const GET_KPI_VALUES_QUERY = `
  query GetKPIValuesV6(
    $dimensionType: String!
    $dimensionTimeframe: String!
    $dimensionValue: String!
    $nextToken: String
    $limit: Int
    $sortColumn: String
    $sortOrder: String
    $circleLevelID: String
    $circleID: String
    $viewID: String
    $parentCircleID: String
  ) {
    getKPIValuesV6(
      dimensionType: $dimensionType
      dimensionTimeframe: $dimensionTimeframe
      dimensionValue: $dimensionValue
      nextToken: $nextToken
      limit: $limit
      sortColumn: $sortColumn
      sortOrder: $sortOrder
      circleLevelID: $circleLevelID
      circleID: $circleID
      viewID: $viewID
      parentCircleID: $parentCircleID
    )
  }
`;

const GET_USERS_STATUS_QUERY = `
  query SearchUsers($orgID: ID!, $nextToken: String) {
    searchUsers(
      filter: {
        orgID: { eq: $orgID }
      }
      limit: 1000
      nextToken: $nextToken
    ) {
      items {
        id
        name
        status
      }
      nextToken
    }
  }
`;

interface DownloadCSVButtonProps {}

const DownloadCSVButton: React.FC<DownloadCSVButtonProps> = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const { leaderboardFilters } = useLeaderboardStore();

  // Fetch all user statuses to filter out deleted users
  const fetchUserStatuses = async (): Promise<{ [userId: string]: string }> => {
    const userStatuses: { [userId: string]: string } = {};
    let nextToken = '';

    do {
      const apiResult = (await API.graphql({
        query: GET_USERS_STATUS_QUERY,
        variables: {
          orgID: (global as any).me.orgID,
          nextToken,
        },
      })) as any;

      const users = apiResult.data.searchUsers.items;
      users.forEach((user: any) => {
        userStatuses[user.id] = user.status;
      });

      nextToken = apiResult.data.searchUsers.nextToken || '';
    } while (nextToken);

    return userStatuses;
  };

  const fetchAllLeaderboardData = async (): Promise<{
    results: Result[];
    kpiList: { [key: string]: any };
  }> => {
    if (!leaderboardFilters) {
      throw new Error('No leaderboard filters available');
    }

    const allResults: Result[] = [];
    let nextToken = '';
    let kpiList: { [key: string]: any } = {};
    let isFirstPage = true;
    let pageCount = 0;

    do {
      pageCount++;
      setDownloadProgress(pageCount);
      const variables = {
        dimensionType: leaderboardFilters.dimensionType,
        dimensionTimeframe: leaderboardFilters.dimensionTimeframe,
        dimensionValue: leaderboardFilters.dimensionValue,
        nextToken,
        limit: 250, // Use maximum limit to reduce API calls
        sortColumn: leaderboardFilters.sortBy,
        sortOrder: leaderboardFilters.sortOrder,
        circleLevelID: leaderboardFilters.circleLevelID,
        circleID: leaderboardFilters.circleID,
        parentCircleID: leaderboardFilters.parentCircleID,
        viewID: leaderboardFilters.viewID,
      };

      const apiResult = (await API.graphql({
        query: GET_KPI_VALUES_QUERY,
        variables,
      })) as any;

      const parsedResult = JSON.parse(apiResult.data.getKPIValuesV6);

      // Store KPI list from first page
      if (isFirstPage) {
        kpiList = parsedResult.kpiList;
        isFirstPage = false;
      }

      // Add results to our collection
      allResults.push(...parsedResult.results);

      // Update nextToken for next iteration
      nextToken = parsedResult.nextToken || '';
    } while (nextToken);

    return {
      results: allResults,
      kpiList,
    };
  };

  const handleDownloadCSV = async () => {
    if (!leaderboardFilters) {
      alert(
        'No leaderboard configuration available. Please try refreshing the page.'
      );
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      // Fetch user statuses to filter out deleted users
      const userStatuses = await fetchUserStatuses();

      // Fetch all data across all pages
      const allData = await fetchAllLeaderboardData();

      if (!allData.results || allData.results.length === 0) {
        console.warn('No data available for export.');
        return;
      }

      // Filter out deleted users
      const filteredResults = allData.results.filter((result: Result) => {
        const userStatus = userStatuses[result.id];
        return userStatus !== 'deleted';
      });

      if (filteredResults.length === 0) {
        console.warn('No active users found for export.');
        return;
      }

      const csvRows = [];

      // Create headers: Rank, Name, and all KPI names
      const headers = ['Rank', 'Name', ...Object.keys(allData.kpiList || {})];
      csvRows.push(headers.join(','));

      // Sort all results alphabetically by name
      const sortedResults = [...filteredResults].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );

      // Convert row data to CSV format
      sortedResults.forEach((row: Result) => {
        const values = [
          row.rank,
          `"${row.name.replace(/"/g, '""')}"`, // Escape quotes in name
          ...Object.keys(allData.kpiList || {}).map((kpiKey) => {
            const value = row.kpis[kpiKey];
            // Handle undefined/null values and escape quotes
            if (value === undefined || value === null) {
              return '""';
            }
            return `"${String(value).replace(/"/g, '""')}"`;
          }),
        ];
        csvRows.push(values.join(','));
      });

      // Create CSV string and trigger download
      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `Leaderboard_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Show success message
      console.log(
        `Successfully downloaded ${
          sortedResults.length
        } records (filtered out ${
          allData.results.length - filteredResults.length
        } deleted users)`
      );
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      variant="contained"
      style={{
        backgroundColor: '#ff6a00',
        height: 40,
        minWidth: 140,
        position: 'relative',
      }}
      onClick={handleDownloadCSV}
      disabled={isDownloading}
    >
      {isDownloading ? (
        <>
          <CircularProgress
            size={16}
            style={{
              color: 'white',
              marginRight: 8,
            }}
          />
          {downloadProgress > 0
            ? `Page ${downloadProgress}...`
            : 'Downloading...'}
        </>
      ) : (
        'Download CSV'
      )}
    </Button>
  );
};

export default DownloadCSVButton;
